import { ActionButton, CardFrame } from '../parts/index.js';
// The item page's "Who may see this", with Change (pipeline R3, REQ-2026-10-05-18; module 10 U315 to U318). Mike,
// 2026-09-24, by pop-up: "Beside the line that says who may see the item, a "Change" link offering the same choices as a
// question card. Your pick re-files the item and is recorded as your correction, which teaches Cobrain."
//
// Change is drawn only where the API sets `refile`: a message item, read by the person a tier question on it is put to
// (U316; apps/api/src/routes/item.ts, app.may_refile_signal in migration 211). The choices are the card's own chooser
// (Questions.tsx TierChoices): select one tier, or two when it is honestly both, then Re-file; or "it is mine" under a
// personal area; or not relevant. The tier filed now leads, tagged "Filed now" (U318). The answer goes to
// POST /api/privacy-flags/refile/:signalId, which raises the owner's own question on this one item and answers it as
// the card is answered, so the pick re-files this item and nothing else, and writes the correction Cobrain learns from.
// "Not relevant" here is about this item alone, so its sentence leaves out the card's "stops asking about this sender".
// After a tier, the page reads the item again and the line says the new tier; after "it is mine" the item has left this
// store, and the page goes back to the list.
import { useEffect, useState } from 'react';
import { api, ApiError, Unauthenticated, type Answer, type PersonalArea } from '../api.js';
import { tierName } from '../labels.js';
import { TierChoices } from '../Questions.js';

export interface Refile { signalId: string; tier: string; alsoTier: string | null }

export function ItemChange({ storeId, line, refile, said, onRefiled, onMoved }: {
  storeId: string;
  line: string;
  refile: Refile | null;
  /** What the last re-file did, kept by the page: the page reads the item again after it, which redraws this panel. */
  said: string | null;
  onRefiled: (said: string) => void;
  onMoved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [areas, setAreas] = useState<PersonalArea[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    api.personalAreas(storeId).then((r) => { if (live) setAreas(r.areas); }).catch(() => { if (live) setAreas([]); });
    return () => { live = false; };
  }, [open, storeId]);

  const answer = (a: Answer) => {
    if (!refile || 'personal' in a) return;
    setBusy(true); setError(null);
    api.refile(storeId, refile.signalId, a)
      .then(() => {
        setOpen(false);
        if ('personalArea' in a) { onMoved(); return; }
        onRefiled('tier' in a
          ? `Re-filed under ${tierName(a.tier)}${a.alsoTier ? `, also ${tierName(a.alsoTier)}` : ''}. Recorded as your correction.`
          : 'Marked not relevant to you. Recorded as your correction.');
      })
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
      })
      .finally(() => setBusy(false));
  };

  return (
    <CardFrame variant="panel">
      <h2>Who may see this</h2>
      <p className="rule">
        {line}
        {refile ? <>{' '}<ActionButton reading type="button" className="link inline" aria-expanded={open} disabled={busy} onClick={() => { setOpen((o) => !o); setError(null); }}>{open ? 'cancel' : 'Change'}</ActionButton></> : null}
      </p>
      {open && refile ? (
        <div className="item-change">
          <TierChoices
            key={`${refile.tier}:${refile.alsoTier ?? ''}`}
            offer={{ offered: [refile.tier, ...(refile.alsoTier ? [refile.alsoTier] : [])], leadTag: 'Filed now', recommendedTier: null, recommendedFrom: null,
                     submitLabel: 'Re-file', notRelevantGist: 'Kept and searchable, never in triage.' }}
            areas={areas} busy={busy} onAnswer={answer} />
          <p className="note">Everyone on this message sees it either way — the tier only decides who else can find it.</p>
        </div>
      ) : null}
      {said && !open ? <p className="note said" role="status">{said}</p> : null}
      {error ? <p className="note" role="alert">That did not go through: {error.replace(/\.$/, '')}.</p> : null}
    </CardFrame>
  );
}
